/**
 * Las sesiones A, B y C de Fernando: los bloques «lunes», «martes» y «jueves»
 * de la tabla semanal de docs/Entrenamiento/ENTRENAMIENTO FERNANDO.numbers
 * (Tabla 1). Esos nombres identifican bloques de rutina, no días fijos
 * (docs/DUOFIT_FORMULARIO_INICIAL_PROPUESTA.md, sección 5): las sesiones van
 * sin días y Gym propone la siguiente al terminar cada una. El bloque
 * «viernes o sábado» no entra.
 *
 * - Ejercicios, series y repeticiones tal como vienen en la tabla. Sin cargas:
 *   Fernando registra las reales.
 * - Descansos confirmados (26 oct 2026): 2 min en todos, salvo el peso muerto,
 *   3 min (como en su registro: "descanso implementado a 3min").
 * - No se agregan los que dependen de la polea ni los nombres por confirmar:
 *   quedan en PENDIENTES_DE_FERNANDO, sin adivinar el ejercicio. Por eso esas
 *   biseries (E1 y E2) todavía no se arman.
 * - El curl va como lo dice la tabla ("Curl bíceps barra Z"); su registro dice
 *   "Curl predicador", así que también se pregunta, sin cambiarlo.
 */

import type { EjercicioDePlantilla, PendienteDePlantilla, RutinaDePlantilla } from './rutinas-precargadas';

export const GENERAL_DE_FERNANDO =
  'Descansos confirmados: 2 min en todos los ejercicios y 3 min en el peso muerto. En el gym los puedes ajustar o saltar en cada serie.';

/** Descansos confirmados de Fernando en el gimnasio (26 oct 2026). */
export const DESCANSO_DE_FERNANDO_SEG = 120;
export const DESCANSO_PESO_MUERTO_SEG = 180;

const ejercicio = (
  exerciseId: string,
  series: number,
  repsMin: number | null,
  repsMax: number | null,
  extra: { descansoSeg?: number; notas?: string; nombreEnArchivo?: string } = {},
): EjercicioDePlantilla => ({
  exerciseId,
  series,
  repsMin,
  repsMax,
  descansoSeg: extra.descansoSeg ?? DESCANSO_DE_FERNANDO_SEG,
  biserie: null,
  descansoBiserieSeg: null,
  notas: extra.notas ?? null,
  ...(extra.nombreEnArchivo === undefined ? {} : { nombreEnArchivo: extra.nombreEnArchivo }),
});

const SIN_DIAS = 'Sin días fijos: al terminar esta sesión, Gym te propone la siguiente.';

export const RUTINA_DE_FERNANDO: readonly RutinaDePlantilla[] = [
  {
    id: 'fernando.a',
    nombre: 'Sesión A · Espalda y tríceps',
    dias: [],
    notas: `Bloque «Lunes · espalda y tríceps» de tu tabla. ${SIN_DIAS}`,
    ejercicios: [
      ejercicio('ejb_press_banca_barra', 3, 7, 9, { nombreEnArchivo: 'Press banca' }),
      ejercicio('ejb_remo_barra', 3, 7, 9, {
        notas: 'Espalda recta todo el tiempo y rodillas un poco flexionadas. Al subir, contrae 2 segundos; baja controlado en 3 segundos.',
      }),
      ejercicio('ejb_press_hombro_mancuernas', 2, 10, 12, {
        nombreEnArchivo: 'Press militar c/ mancuerna',
        notas: 'Sentado, con los codos ligeramente cerrados y sin juntar las mancuernas arriba.',
      }),
      ejercicio('ejb_jalon_pecho', 2, 10, 12, { notas: 'Con agarre supino.' }),
    ],
  },
  {
    id: 'fernando.b',
    nombre: 'Sesión B · Pierna',
    dias: [],
    notas: `Bloque «Martes» de tu tabla. ${SIN_DIAS}`,
    ejercicios: [
      ejercicio('ejb_sentadilla_barra', 3, 7, 9, {
        nombreEnArchivo: 'Sentadilla back',
        notas: 'Pocas repeticiones. Para glúteos, recárgate hacia atrás.',
      }),
      ejercicio('ejb_peso_muerto_barra', 3, 9, 11, {
        descansoSeg: DESCANSO_PESO_MUERTO_SEG,
        notas: 'Pocas repeticiones. Descanso de 3 min, como en tu registro.',
      }),
      ejercicio('ejb_prensa', 2, 10, 12, { nombreEnArchivo: 'Prensa' }),
      ejercicio('ejb_desplante_estatico', 2, 10, 12, {
        nombreEnArchivo: 'Desplantes estáticos',
        notas: 'Repeticiones por lado.',
      }),
      ejercicio('ejb_pantorrilla_sentado_maquina', 3, 16, 20, {
        nombreEnArchivo: 'Costurera',
        notas: 'En tu tabla va en biserie con «Elevaciones de pie»: se junta cuando confirmes ese ejercicio.',
      }),
    ],
  },
  {
    id: 'fernando.c',
    nombre: 'Sesión C · Hombro, espalda y bíceps',
    dias: [],
    notas: `Bloque «Jueves» de tu tabla. ${SIN_DIAS}`,
    ejercicios: [
      ejercicio('ejb_press_militar_barra', 3, 10, 12),
      ejercicio('ejb_dominadas_negativas', 3, null, null, { notas: 'Cada serie al fallo. Puede ser con agarre supino.' }),
      ejercicio('ejb_remo_mancuerna', 2, 10, 12, { nombreEnArchivo: 'Remo c/ mancuerna' }),
      ejercicio('ejb_curl_barra_z', 2, 10, 12, {
        nombreEnArchivo: 'Curl bíceps barra Z (rep. altas)',
        notas: 'En tu tabla va en biserie con las elevaciones laterales con polea: se junta cuando confirmes la polea.',
      }),
    ],
  },
];

/** Lo que la tabla trae y todavía no se agrega: se pregunta a Fernando, sin adivinar. */
export const PENDIENTES_DE_FERNANDO: readonly PendienteDePlantilla[] = [
  {
    sesion: 'Sesión A',
    texto: 'E1. Extensión tríceps 2 × 12–14 y E2. Face pull 2 × 12–14 (biserie)',
    pregunta: '¿Hay polea en tu gimnasio actual? En tu registro también aparece la extensión de tríceps con mancuerna sobre la cabeza.',
  },
  {
    sesion: 'Sesión B',
    texto: 'E2. Elevaciones de pie 3 × 9–11 (biserie con la costurera)',
    pregunta: '¿Son elevaciones de pantorrilla de pie? ¿Con mancuernas o en máquina?',
  },
  {
    sesion: 'Sesión B',
    texto: '*F. Swiss crunch 3 al fallo y Leg raise 4 × 15–20',
    pregunta: '¿Van en la sesión? ¿Qué ejercicio es el Swiss crunch?',
  },
  {
    sesion: 'Sesión C',
    texto: 'C. Press barra inverso 2 × 10–12',
    pregunta: '¿Qué ejercicio es? En tu registro aparece junto a «Press inclinado con mancuernas».',
  },
  {
    sesion: 'Sesión C',
    texto: 'E1. Curl bíceps barra Z (rep. altas) 2 × 10–12',
    pregunta:
      'Ya está en la sesión como «Curl con barra Z». En tu registro dice «Curl predicador bíceps barra Z»: ¿lo haces en banco predicador?',
  },
  {
    sesion: 'Sesión C',
    texto: 'E2. Elevaciones laterales con polea 2 × 10–12 (biserie con el curl)',
    pregunta: 'Depende de la polea.',
  },
];
